'use client';

import { c as _c } from "react/compiler-runtime";
import type { ExperimentClient } from '@amplitude/experiment-js-client';
import { BuilderBlocks } from '@builder.io/react';
import { uniqueId } from 'lodash';
import { useSearchParams } from 'next/navigation';
import { type ReactNode } from 'react';
import React from 'react';
import { BODEnabledConditionalContent } from '@src/components/ConditionalBox/BODEnabledConditionalContent';
import { BODiAccessConditionalContent } from '@src/components/ConditionalBox/BODiAccessConditionalContent';
import { LoggedInConditionalContent } from '@src/components/ConditionalBox/LoggedInConditionalContent';
import { ShopifyEnabledConditionalContent } from '@src/components/ConditionalBox/ShopifyEnabledConditionalContent';
import { useLocale } from '@src/i18n/LocaleProvider';
import { getBuilderPreviewMode } from '@src/utils/builder.util';
import { useExperimentContext } from '@src/utils/experimentProvider';
import { cn } from '@src/utils/tailwind';
export interface SkuFilter {
  skus: string[];
  andOr: 'Any' | 'All';
}
type ContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode[] | ReactNode;
  includedChildren?: any[];
  visibilityBasedOn?: 'locale' | 'experiment' | 'queryParams' | 'loggedIn' | 'hasBodiAccess' | 'sku' | 'shopifyEnabled' | 'bodEnabled';
  attributes?: any;
  experimentFlag: string;
  experimentVariant: string;
  showAsOff: boolean;
  showIfLoggedIn: boolean;
  showIfHasBodiAccess: boolean;
  showIfShopifyEnabled: boolean;
  showIfBODEnabled: boolean;
  queryParams: string;
  matchType: string;
  skusInOut: boolean;
  anyOrAll: 'Any' | 'All';
  skus: string[];
};
// Checks if user is in experiment and variant, and decides whether to render content in box.
const isUserInExperiment = (experimentList: any, flag: string, variant: string,
// builder value
showAsOff: boolean = false,
// builder value
experimentClient: ExperimentClient | null = null // useExperimentContext.experimentClient
) => {
  let showExperiment = false;
  // Compare conditional box's flag to list of flags from amplitude
  const flags = Object.keys(experimentList);
  const userInExperiment = flags.includes(flag);
  // Check if variant matches user's assigned variant.

  const userInVariant = Object.prototype.hasOwnProperty.call(experimentList, flag) ? experimentList[flag].key === variant : false;

  /*  If user is in experiment and variant, show the conditional box and fire exposure event.
  Else, if Show As Off is true and user is not in experiment(it was not rolled out), show the box.
  */

  if (userInExperiment && userInVariant) {
    showExperiment = true;
    if (experimentClient) {
      exposeExperiment(experimentClient, flag);
    }
  } else if (!userInExperiment && showAsOff) {
    showExperiment = true;
  }
  return showExperiment;
};
const exposeExperiment = (experimentClient: ExperimentClient, flag: string) => {
  experimentClient.variant(flag);
  experimentClient.exposure(flag);
};
const experimentLabel = (flag: string, variant: string) => {
  return <div className="pointer-events-none absolute top-0 right-9 z-50 bg-yellow-600 px-2 py-4 text-body-base text-black opacity-70" key={flag + '.' + variant}>
      Experiment:{flag}:{variant}
    </div>;
};
const queryLabel = (queryParam: string) => {
  return <div className="pointer-events-none absolute top-0 right-9 z-50 bg-green-600 px-2 py-4 text-body-base text-black opacity-70" key={queryParam}>
      Query Param:{queryParam}
    </div>;
};
export const ConditionalBox = t0 => {
  const $ = _c(61);
  const {
    className,
    children,
    visibilityBasedOn,
    includedChildren,
    experimentFlag,
    experimentVariant,
    showAsOff,
    showIfLoggedIn,
    showIfHasBodiAccess,
    showIfShopifyEnabled,
    showIfBODEnabled,
    queryParams,
    matchType,
    skusInOut,
    anyOrAll,
    skus,
    ...props
  } = t0;
  const {
    builderBlock,
    attributes
  } = props as any;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== children) {
    t1 = () => React.Children.count(children) <= 0;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const shouldRenderBuilderBlocks = t1;
  const experimentContext = useExperimentContext();
  const userExperiments = experimentContext?.allVariants;
  const {
    locale
  } = useLocale();
  const isLocationHidden = (props as any)?.[`locale_${locale.path}`];
  const {
    edit
  } = getBuilderPreviewMode();
  const hasQueryParam = (matchType_0, queryParam) => {
    const search = searchParams.toString();
    switch (matchType_0) {
      case "contains":
        {
          return search.search(queryParam) !== -1;
        }
      case "does not contain":
        {
          return search.search(queryParam) === -1;
        }
      case "regEx":
        {
          const regEx = new RegExp(queryParam, "gi");
          return regEx.test(search);
        }
      default:
    }
  };
  const localeConditionalContent = <div {...attributes} key={attributes?.key} className={cn("size-auto", className, attributes?.className, {
    "grayscale opacity-35": isLocationHidden && edit,
    hidden: isLocationHidden && !edit
  })} style={{
    ...attributes?.style
  }} suppressHydrationWarning={true}>{shouldRenderBuilderBlocks() ? includedChildren?.map((child, index) => <BuilderBlocks key={uniqueId()} parentElementId={builderBlock?.id} dataPath={`component.options.includedChildren.${index}.item`} blocks={child.item} style={{
      minHeight: "auto !important"
    }} />) : children}</div>;
  const t2 = attributes?.key;
  const t3 = cn("size-auto", className, attributes?.className, {
    "border-4 border-green-600 relative": queryParams && edit
  });
  const t4 = attributes?.style;
  let t5;
  if ($[2] !== t4) {
    t5 = {
      ...t4
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== edit || $[5] !== queryParams) {
    t6 = edit && queryLabel(queryParams);
    $[4] = edit;
    $[5] = queryParams;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== builderBlock?.id || $[8] !== children || $[9] !== includedChildren || $[10] !== shouldRenderBuilderBlocks) {
    t7 = shouldRenderBuilderBlocks() ? includedChildren?.map((child_0, index_0) => <BuilderBlocks key={uniqueId()} parentElementId={builderBlock?.id} dataPath={`component.options.includedChildren.${index_0}.item`} blocks={child_0.item} style={{
      minHeight: "auto !important"
    }} />) : children;
    $[7] = builderBlock?.id;
    $[8] = children;
    $[9] = includedChildren;
    $[10] = shouldRenderBuilderBlocks;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== attributes || $[13] !== t2 || $[14] !== t3 || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div {...attributes} key={t2} className={t3} style={t5} suppressHydrationWarning={true}>{t6}{t7}</div>;
    $[12] = attributes;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const queryParamsConditionalContent = t8;
  const t9 = attributes?.key;
  const t10 = attributes?.className;
  const t11 = experimentFlag && edit;
  let t12;
  if ($[19] !== className || $[20] !== t10 || $[21] !== t11) {
    t12 = cn("size-auto", className, t10, {
      "border-4 border-yellow-600 relative": t11
    });
    $[19] = className;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = attributes?.style;
  let t14;
  if ($[23] !== t13) {
    t14 = {
      ...t13
    };
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== edit || $[26] !== experimentFlag || $[27] !== experimentVariant) {
    t15 = edit && experimentLabel(experimentFlag, experimentVariant);
    $[25] = edit;
    $[26] = experimentFlag;
    $[27] = experimentVariant;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== builderBlock?.id || $[30] !== children || $[31] !== includedChildren || $[32] !== shouldRenderBuilderBlocks) {
    t16 = shouldRenderBuilderBlocks() ? includedChildren?.map((child_1, index_1) => <BuilderBlocks key={uniqueId()} parentElementId={builderBlock?.id} dataPath={`component.options.includedChildren.${index_1}.item`} blocks={child_1.item} style={{
      minHeight: "auto !important"
    }} />) : children;
    $[29] = builderBlock?.id;
    $[30] = children;
    $[31] = includedChildren;
    $[32] = shouldRenderBuilderBlocks;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== attributes || $[35] !== t12 || $[36] !== t14 || $[37] !== t15 || $[38] !== t16 || $[39] !== t9) {
    t17 = <div {...attributes} key={t9} className={t12} style={t14} suppressHydrationWarning={true}>{t15}{t16}</div>;
    $[34] = attributes;
    $[35] = t12;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t9;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const experimentConditionalContent = t17;
  bb0: switch (visibilityBasedOn) {
    case "experiment":
      {
        if (experimentFlag && experimentVariant) {
          const isExperimentVisible = isUserInExperiment(userExperiments, experimentFlag, experimentVariant, showAsOff, experimentContext?.experimentClient);
          if (isExperimentVisible || edit) {
            return experimentConditionalContent;
          }
        }
        break bb0;
      }
    case "queryParams":
      {
        if (hasQueryParam(matchType, queryParams) || edit) {
          return queryParamsConditionalContent;
        }
        break bb0;
      }
    case "locale":
      {
        return localeConditionalContent;
      }
    case "loggedIn":
      {
        let t18;
        if ($[41] !== attributes || $[42] !== children || $[43] !== className || $[44] !== showIfLoggedIn) {
          t18 = <LoggedInConditionalContent attributes={attributes} className={className} showIfLoggedIn={showIfLoggedIn}>{children}</LoggedInConditionalContent>;
          $[41] = attributes;
          $[42] = children;
          $[43] = className;
          $[44] = showIfLoggedIn;
          $[45] = t18;
        } else {
          t18 = $[45];
        }
        return t18;
      }
    case "hasBodiAccess":
      {
        let t18;
        if ($[46] !== attributes || $[47] !== children || $[48] !== className || $[49] !== showIfHasBodiAccess) {
          t18 = <BODiAccessConditionalContent attributes={attributes} className={className} showIfHasBodiAccess={showIfHasBodiAccess}>{children}</BODiAccessConditionalContent>;
          $[46] = attributes;
          $[47] = children;
          $[48] = className;
          $[49] = showIfHasBodiAccess;
          $[50] = t18;
        } else {
          t18 = $[50];
        }
        return t18;
      }
    case "shopifyEnabled":
      {
        let t18;
        if ($[51] !== attributes || $[52] !== children || $[53] !== className || $[54] !== showIfShopifyEnabled) {
          t18 = <ShopifyEnabledConditionalContent attributes={attributes} className={className} showIfShopifyEnabled={showIfShopifyEnabled}>{children}</ShopifyEnabledConditionalContent>;
          $[51] = attributes;
          $[52] = children;
          $[53] = className;
          $[54] = showIfShopifyEnabled;
          $[55] = t18;
        } else {
          t18 = $[55];
        }
        return t18;
      }
    case "bodEnabled":
      {
        let t18;
        if ($[56] !== attributes || $[57] !== children || $[58] !== className || $[59] !== showIfBODEnabled) {
          t18 = <BODEnabledConditionalContent attributes={attributes} className={className} showIfBODEnabled={showIfBODEnabled}>{children}</BODEnabledConditionalContent>;
          $[56] = attributes;
          $[57] = children;
          $[58] = className;
          $[59] = showIfBODEnabled;
          $[60] = t18;
        } else {
          t18 = $[60];
        }
        return t18;
      }
    default:
      {
        return null;
      }
  }
};